export const MEDIA_SCAN_QUEUE = 'media-malware-scan';

export type MediaScanJob = {
  assetId: string;
};

export const MediaScanStatus = {
  PENDING: 'pending',
  QUEUED: 'queued',
  SCANNING: 'scanning',
  CLEAN: 'clean',
  INFECTED: 'infected',
  ERROR: 'error',
  SKIPPED: 'skipped',
} as const;

export type MediaScanStatusValue =
  (typeof MediaScanStatus)[keyof typeof MediaScanStatus];

const startsWith = (bytes: Uint8Array, signature: number[], offset = 0) =>
  signature.every((value, index) => bytes[offset + index] === value);

const ascii = (bytes: Uint8Array, offset: number, length: number) =>
  String.fromCharCode(...bytes.slice(offset, offset + length));

/**
 * Detects the supported upload formats from content signatures. Text is only
 * accepted when it contains no NUL bytes; callers still enforce purpose/MIME.
 */
export function detectUploadMimeType(bytes: Uint8Array): string | null {
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (ascii(bytes, 0, 6) === 'GIF87a' || ascii(bytes, 0, 6) === 'GIF89a') return 'image/gif';
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') return 'image/webp';
  if (ascii(bytes, 0, 5) === '%PDF-') return 'application/pdf';
  if (ascii(bytes, 4, 4) === 'ftyp') return 'video/mp4';
  if (startsWith(bytes, [0x1a, 0x45, 0xdf, 0xa3])) return 'video/webm';
  if (ascii(bytes, 0, 4) === 'OggS') return 'audio/ogg';
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WAVE') return 'audio/wav';
  if (ascii(bytes, 0, 3) === 'ID3' || (bytes[0] === 0xff && (bytes[1] ?? 0) >= 0xe0)) {
    return 'audio/mpeg';
  }
  if (startsWith(bytes, [0x00, 0x00, 0x00]) && ascii(bytes, 4, 4) === 'ftyp') {
    return 'audio/mp4';
  }
  if (startsWith(bytes, [0x50, 0x4b, 0x03, 0x04])) {
    const zipPrefix = ascii(bytes, 0, bytes.length);
    if (zipPrefix.includes('[Content_Types].xml') && zipPrefix.includes('word/')) {
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }
    if (zipPrefix.includes('[Content_Types].xml') && zipPrefix.includes('xl/')) {
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    return 'application/zip';
  }
  if (startsWith(bytes, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) {
    return 'application/x-ole-storage';
  }
  if (bytes.length > 0 && !bytes.some((value) => value === 0)) return 'text/plain';
  return null;
}

const MIME_EQUIVALENTS: Readonly<Record<string, readonly string[]>> = {
  'text/vtt': ['text/plain'],
  'video/quicktime': ['video/mp4'],
  'audio/webm': ['video/webm'],
  'audio/mp4': ['video/mp4', 'audio/mp4'],
  'application/msword': ['application/x-ole-storage'],
  'application/vnd.ms-excel': ['application/x-ole-storage'],
};

export function uploadMimeMatches(declaredMime: string, detectedMime: string): boolean {
  return declaredMime === detectedMime || MIME_EQUIVALENTS[declaredMime]?.includes(detectedMime) === true;
}
